import { cloneDeep, omit } from "lodash";
import { applyPatch, createPatch } from "rfc6902";
import { getFromDataPath, mutateByPath, removeByPath } from "../../utility";
import {
  AnalysisSet,
  DataSubset,
  ExpressionLogicalOperatorEnum,
  GroupingFactor,
  SubClauseWithCondition,
  WhereClauseCompoundExpression,
  WhereClauseWithCompoundExpression,
} from "../ars_ldm";
import {
  isSubClauseWithCompoundExpression,
  isSubClauseWithCondition,
  isWhereClause,
  isWhereClauseWithCompoundExpression,
  isWhereClauseWithCondition,
} from "../typeGuards";
import {
  ADD_ANALYSIS_SET,
  ADD_DATA_SUBSET,
  ADD_EXTRA_WHERE_CLAUSE,
  ADD_GROUPING_FACTOR,
  ADD_NOT_TO_WHERE_CLAUSE,
  ARS_CHANGE_SENT,
  ARSContextState,
  ARSDataState,
  ARSPatch,
  IARSAction,
  IARSActionPatchARSStateSingle,
  LOAD_ANALYSIS_SETS,
  LOAD_DATA_SUBSETS,
  LOAD_GROUPING_FACTORS,
  LOAD_REFERENCE_DATA,
  PATCH_ARS_STATE,
  REDO_ARS_CHANGE,
  REMOVE_ARS_ITEM,
  REMOVE_NOT_FROM_WHERE_CLAUSE,
  REMOVE_WHERE_CLAUSE,
  SET_UI_REQUEST_STATUS,
  UNDO_ARS_CHANGE,
} from "./interface";
import { removeWhereClause } from "./removeWhereClause";

/**
 * Extracts the data state from the full context state (excluding undo/redo metadata).
 *
 * @param state - The full ARSContextState
 * @returns The ARSDataState without undo/redo tracking
 */
const extractDataState = (state: ARSContextState): ARSDataState => ({
  variables: state.variables,
  comparators: state.comparators,
  analysisSets: state.analysisSets,
  dataSubsets: state.dataSubsets,
  groupingFactors: state.groupingFactors,
  uiRequestStatus: state.uiRequestStatus,
  changed: state.changed,
});

/**
 * Adds a patch to the undo/redo stack based on state changes.
 *
 * @param previousState - The previous ARSContextState
 * @param newState - The new ARSContextState after changes
 * @returns The updated state with the new patch added to the stack
 */
const addPatchToStack = (
  previousState: ARSContextState,
  newState: ARSContextState,
): ARSContextState => {
  const previousData = extractDataState(previousState);
  const newData = extractDataState(newState);

  // Create forward and reverse patches
  const forwardPatch = createPatch(previousData, newData);
  const reversePatch = createPatch(newData, previousData);

  // If no changes, don't add to stack
  if (forwardPatch.length === 0) {
    return newState;
  }

  const newPatch: ARSPatch = {
    forward: forwardPatch,
    reverse: reversePatch,
  };

  // Discard any patches after the current index (redo history is lost)
  const newPatches = [...previousState.patches.slice(0, previousState.ixPatch + 1), newPatch];

  return {
    ...newState,
    patches: newPatches,
    ixPatch: newPatches.length - 1,
  };
};

/**
 * Reducer for ARS context state transitions.
 *
 * @param state - The current ARSContextState
 * @param action - The IARSAction describing the update to apply
 * @returns The updated ARSContextState after applying the action
 */
export const arsContextReducer = (state: ARSContextState, action: IARSAction): ARSContextState => {
  switch (action.operation) {
    case ADD_ANALYSIS_SET:
      if (Object.keys(state.analysisSets).length === action.newLength - 1) {
        const newSet: AnalysisSet = {
          id: crypto.randomUUID(),
          name: `AnalysisSet_${action.newLength}`,
          label: `Analysis Set ${action.newLength}`,
          description: "",
          condition: {},
          level: 0,
          order: 0,
        };
        return addPatchToStack(state, {
          ...state,
          analysisSets: { ...state.analysisSets, [newSet.id as string]: newSet },
          changed: true,
        });
      } else return state;

    case ADD_DATA_SUBSET:
      if (Object.keys(state.dataSubsets).length === action.newLength - 1) {
        const newSubset: DataSubset = {
          id: crypto.randomUUID(),
          name: `DataSubset_${action.newLength}`,
          label: `Data Subset ${action.newLength}`,
          description: "",
          condition: {},
          level: 0,
          order: 0,
        };
        return addPatchToStack(state, {
          ...state,
          dataSubsets: { ...state.dataSubsets, [newSubset.id as string]: newSubset },
          changed: true,
        });
      } else return state;

    case ADD_EXTRA_WHERE_CLAUSE: {
      const whereClause = getFromDataPath(action.dataPath, state, isWhereClause);
      if (!whereClause) {
        throw new Error(`Invalid dataPath for ADD_EXTRA_WHERE_CLAUSE: ${action.dataPath}`);
      } else if (isWhereClauseWithCondition(whereClause)) {
        // Convert to compound expression with existing condition as first clause
        const newCompoundExpression = {
          logicalOperator: ExpressionLogicalOperatorEnum.AND,
          whereClauses: [
            {
              id: crypto.randomUUID(),
              subClauseId: "",
              condition: whereClause.condition,
              level: whereClause.level + 1,
              order: 0,
            },
            {
              id: crypto.randomUUID(),
              subClauseId: "",
              condition: {},
              level: whereClause.level + 1,
              order: 1,
            },
          ],
        };
        const newEntity = {
          ...omit(whereClause, "condition"),
          compoundExpression: newCompoundExpression,
          level: whereClause.level,
          order: whereClause.order,
        };
        return addPatchToStack(
          state,
          mutateByPath<ARSContextState>({ ...state, changed: true }, action.dataPath, newEntity),
        );
      } else {
        const ce = whereClause.compoundExpression;
        // Add new clause to existing compound expression
        const newClause: SubClauseWithCondition = {
          id: crypto.randomUUID(),
          subClauseId: "",
          condition: {},
          level:
            ce.whereClauses?.[0]?.level ??
            // istanbul ignore next
            1,
          order: (
            ce.whereClauses ??
            // istanbul ignore next
            []
          ).length,
        };
        const newWhereClauses = [
          ...(ce.whereClauses ??
            // istanbul ignore next
            []),
          newClause,
        ];
        const newCompoundExpression = {
          ...ce,
          logicalOperator:
            ce.logicalOperator === ExpressionLogicalOperatorEnum.NOT
              ? ExpressionLogicalOperatorEnum.AND
              : ce.logicalOperator,
          whereClauses: newWhereClauses,
        };
        return addPatchToStack(
          state,
          mutateByPath<ARSContextState>(
            { ...state, changed: true },
            `${action.dataPath}/compoundExpression`,
            newCompoundExpression,
          ),
        );
      }
    }

    case ADD_GROUPING_FACTOR: {
      if (Object.keys(state.groupingFactors).length === action.newLength - 1) {
        const newFactor: GroupingFactor = {
          id: crypto.randomUUID(),
          name: `GroupingFactor_${action.newLength}`,
          label: `Grouping Factor ${action.newLength}`,
          description: "",
          dataDriven: false,
          groups: [],
        };
        return addPatchToStack(state, {
          ...state,
          groupingFactors: { ...state.groupingFactors, [newFactor.id as string]: newFactor },
          changed: true,
        });
      } else return state;
    }

    case ARS_CHANGE_SENT: {
      return {
        ...state,
        changed: false,
      };
    }

    case ADD_NOT_TO_WHERE_CLAUSE: {
      const whereClause = getFromDataPath(action.dataPath, state, isWhereClause);
      if (!whereClause) {
        throw new Error(`Invalid dataPath for NOT_WHERE_CLAUSE: ${action.dataPath}`);
      } else if (isWhereClauseWithCondition(whereClause)) {
        // Convert to compound expression with NOT operator
        const newCompoundExpression: WhereClauseCompoundExpression = {
          logicalOperator: ExpressionLogicalOperatorEnum.NOT,
          whereClauses: [
            {
              id: crypto.randomUUID(),
              subClauseId: "",
              condition: whereClause.condition,
              level: whereClause.level + 1,
              order: 0,
            },
          ],
        };
        const newEntity = {
          ...omit(whereClause, "condition"),
          compoundExpression: newCompoundExpression,
          level: whereClause.level,
          order: whereClause.order,
        };
        return addPatchToStack(
          state,
          mutateByPath<ARSContextState>({ ...state, changed: true }, action.dataPath, newEntity),
        );
      } else {
        // Compound expression assured by type guard.
        const ce = whereClause.compoundExpression;
        // Check there are 1 or less whereClauses
        if (
          (ce.whereClauses?.length ??
            // istanbul ignore next
            0) > 1
        ) {
          throw new Error(
            `Cannot apply NOT operator to ${action.dataPath} because it has multiple sub-clauses`,
          );
        } else {
          // Convert to compound expression with NOT operator
          const newCompoundExpression: WhereClauseCompoundExpression = {
            logicalOperator: ExpressionLogicalOperatorEnum.NOT,
            whereClauses:
              ce.whereClauses ??
              // istanbul ignore next
              [],
          };
          const newWhereClause: WhereClauseWithCompoundExpression = {
            compoundExpression: newCompoundExpression,
            level: whereClause.level,
            order: whereClause.order,
          };
          return addPatchToStack(
            state,
            mutateByPath<ARSContextState>(
              { ...state, changed: true },
              action.dataPath,
              newWhereClause,
            ),
          );
        }
      }
    }

    // Load actions
    case LOAD_ANALYSIS_SETS:
      return {
        ...state,
        analysisSets: (() => {
          const map: Record<string, (typeof action.analysisSets)[0]> = {};
          action.analysisSets.forEach((set) => (map[set.id] = set));
          return map;
        })(),
      };

    case LOAD_DATA_SUBSETS:
      return {
        ...state,
        dataSubsets: (() => {
          const map: Record<string, (typeof action.dataSubsets)[0]> = {};
          action.dataSubsets.forEach((subset) => (map[subset.id] = subset));
          return map;
        })(),
      };

    case LOAD_GROUPING_FACTORS:
      return {
        ...state,
        groupingFactors: (() => {
          const map: Record<string, (typeof action.groupingFactors)[0]> = {};
          action.groupingFactors.forEach((factor) => (map[factor.id] = factor));
          return map;
        })(),
      };

    case LOAD_REFERENCE_DATA:
      return {
        ...state,
        variables: action.variables,
        comparators: action.comparators,
      };

    case PATCH_ARS_STATE: {
      // Support both single update (pointer/value) and multiple updates (updates array)
      if ("updates" in action && action.updates.length > 0) {
        // Apply multiple updates atomically
        let newState = state;
        for (const update of action.updates) {
          newState = mutateByPath<ARSContextState>(newState, update.dataPath, update.value);
        }
        return addPatchToStack(state, {
          ...newState,
          changed: true,
        });
      } else {
        // Single update (backward compatibility)
        const singleAction = action as IARSActionPatchARSStateSingle;
        const newState = mutateByPath<ARSContextState>(
          state,
          singleAction.dataPath,
          singleAction.value,
        );
        return addPatchToStack(state, {
          ...newState,
          changed: true,
        });
      }
    }

    case REMOVE_ARS_ITEM: {
      return addPatchToStack(
        state,
        removeByPath<ARSContextState>({ ...state, changed: true }, action.dataPath),
      );
    }

    case REMOVE_NOT_FROM_WHERE_CLAUSE: {
      const whereClause = getFromDataPath(action.dataPath, state, isWhereClause);
      if (
        !whereClause ||
        !isWhereClauseWithCompoundExpression(whereClause) ||
        !whereClause.compoundExpression.whereClauses ||
        whereClause.compoundExpression.whereClauses.length > 1
      ) {
        throw new Error(`Invalid dataPath for REMOVE_NOT_FROM_WHERE_CLAUSE: ${action.dataPath}`);
      }
      if (whereClause.compoundExpression.logicalOperator !== ExpressionLogicalOperatorEnum.NOT) {
        throw new Error(
          `Cannot remove NOT operator from ${action.dataPath} because it is not a NOT clause`,
        );
      }
      if (whereClause.compoundExpression.whereClauses.length === 0) {
        // No sub-clauses, this shouldn't happen but handle gracefully
        throw new Error(
          `Cannot remove NOT operator from ${action.dataPath} because it has no sub-clauses`,
        );
      } else {
        // Single sub-clause, promote it to replace the current where clause
        const subClause = whereClause.compoundExpression.whereClauses[0];
        if (isSubClauseWithCondition(subClause)) {
          const newEntity = {
            ...omit(whereClause, "compoundExpression"),
            condition: subClause.condition,
            level: subClause.level - 1,
            order: whereClause.order,
          };
          return addPatchToStack(
            state,
            mutateByPath<ARSContextState>({ ...state, changed: true }, action.dataPath, newEntity),
          );
        } else if (isSubClauseWithCompoundExpression(subClause)) {
          const newWhereClause: WhereClauseWithCompoundExpression = {
            compoundExpression: subClause.compoundExpression,
            level: subClause.level - 1,
            order: whereClause.order,
          };
          return addPatchToStack(
            state,
            mutateByPath<ARSContextState>(
              { ...state, changed: true },
              action.dataPath,
              newWhereClause,
            ),
          );
        } else {
          // SubClauseWithReference - shouldn't happen but handle gracefully
          throw new Error(
            `Cannot remove NOT operator from ${action.dataPath} - sub-clause is a reference`,
          );
        }
      }
    }

    case REMOVE_WHERE_CLAUSE: {
      const removedState = removeWhereClause(state, action.dataPath, action.id);
      // Only add to version stack if state actually changed
      if (removedState !== state) {
        return addPatchToStack(state, removedState);
      }
      return state;
    }

    case SET_UI_REQUEST_STATUS:
      return {
        ...state,
        uiRequestStatus: action.uiRequestStatus,
      };

    case UNDO_ARS_CHANGE: {
      const steps = action.steps ?? 1;
      const newIx = Math.max(-1, state.ixPatch - steps);

      if (newIx === state.ixPatch) {
        return state;
      }

      // Apply reverse patches from current position back to target
      const currentState = cloneDeep(extractDataState(state));
      for (let i = state.ixPatch; i > newIx; i--) {
        const patch = state.patches[i];
        /* istanbul ignore else */
        if (patch) {
          applyPatch(currentState, patch.reverse);
        }
      }

      return {
        ...state,
        ...currentState,
        ixPatch: newIx,
      };
    }

    case REDO_ARS_CHANGE: {
      const steps = action.steps ?? 1;
      const newIx = Math.min(state.patches.length - 1, state.ixPatch + steps);

      if (newIx === state.ixPatch || newIx < 0) {
        return state;
      }

      // Apply forward patches from current position forward to target
      const currentState = cloneDeep(extractDataState(state));
      for (let i = state.ixPatch + 1; i <= newIx; i++) {
        const patch = state.patches[i];
        /* istanbul ignore else */
        if (patch) {
          applyPatch(currentState, patch.forward);
        }
      }

      return {
        ...state,
        ...currentState,
        ixPatch: newIx,
      };
    }
  }
};
